// 모험 목표판(레인 E 2단계, docs/AVATAR-ADVENTURE-PRD.md 2절): 현상금(하루 3)·유니크 사냥 표·도감. 순수·결정적(시간·저장은 인자로).
// 현상금 보상은 골드가 아니라 "그 보스의 득템 판정 1회 추가"(디아블로식, PRD 2.3) — 경제 시뮬(골드·강화석)을 건드리지 않는다.
// 날짜는 일일 장부와 같은 KST 00:00(shared/daily.ts dayIndex). 목록은 날짜 씨앗으로 정해 모두에게 같다.
import { REGION_IDS } from './levels';
import { AVATARS, AVATAR_DROP, CODEX_AURA_ID, avatarsFrom, type AvatarDef } from './avatar';
import { NAMED_UNIQUES, namedFor } from './content/items';
import type { NamedUniqueDef } from './content/types';
import { REGIONS_90D } from './content/world';
import { hashSeed, nextRand, seedRng } from './gear';

export const BOUNTIES_PER_DAY = 3;
/** 지금 게임에 지도가 있는 지역 수(levels REGION_IDS, C2부터 r1~r6) */
export const PLAYABLE_REGIONS = REGION_IDS.length;

export type BossTier = 'mid' | 'final';
export interface BountyDef {
  /** `${날짜}:${스테이지}` — 같은 날 같은 대상은 한 번만 */
  readonly id: string;
  readonly day: number;
  readonly region: number;
  readonly tier: BossTier;
  readonly stageId: string;
  readonly boss: string;
}

function bossTarget(region: number, tier: BossTier): { stageId: string; boss: string } {
  const r = REGIONS_90D[region - 1];
  return { stageId: r.stages[tier === 'mid' ? 4 : 9].id, boss: tier === 'mid' ? r.midBoss : r.finalBoss };
}

/** 오늘의 현상금: 연 지역(1..min(4, 도착한 마을))의 중간보스·지역보스 중 날짜 씨앗으로 3개(후보가 적으면 그만큼) */
export function bountiesFor(day: number, highestTown: number): BountyDef[] {
  const top = Math.max(1, Math.min(PLAYABLE_REGIONS, Math.floor(highestTown)));
  const pool: BountyDef[] = [];
  for (let region = 1; region <= top; region++) for (const tier of ['mid', 'final'] as const) {
    const t = bossTarget(region, tier);
    pool.push({ id: `${day}:${t.stageId}`, day, region, tier, stageId: t.stageId, boss: t.boss });
  }
  // 결정적 섞기(Fisher–Yates, 날짜 씨앗)
  const rng = seedRng(hashSeed(`bounty:${day}`));
  for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(nextRand(rng) * (i + 1)); const t = pool[i]; pool[i] = pool[j]; pool[j] = t; }
  return pool.slice(0, BOUNTIES_PER_DAY);
}
/** 이 스테이지 보스 처치가 오늘 현상금 대상인가(대상이면 그 현상금) */
export function bountyFor(day: number, highestTown: number, stageId: string): BountyDef | undefined {
  return bountiesFor(day, highestTown).find((b) => b.stageId === stageId);
}

// ── 유니크 사냥 표(PRD 2.2) ──
export interface HuntDrop {
  readonly kind: 'unique' | 'avatar';
  readonly id: string;
  readonly name: string;
  /** 보스 한 번 처치당 확률 */
  readonly chance: number;
}
export interface HuntRow {
  readonly region: number;
  readonly regionName: string;
  readonly tier: BossTier;
  readonly boss: string;
  readonly stageId: string;
  readonly playable: boolean;
  readonly drops: readonly HuntDrop[];
}
function uniqueDrop(u: NamedUniqueDef): HuntDrop { return { kind: 'unique', id: u.id, name: u.name, chance: u.source.chance }; }
function avatarDrops(region: number, tier: BossTier): HuntDrop[] {
  const list = avatarsFrom(region, tier);
  // 지역보스 레어는 1.5%로 한 번 굴린 뒤 세 부위 중 하나 → 부위마다 1.5%/3
  return list.map((a: AvatarDef) => ({ kind: 'avatar' as const, id: a.id, name: a.name, chance: AVATAR_DROP[tier] / (tier === 'final' ? list.length : 1) }));
}
/** 지역 1~6 × 중간보스·지역보스의 득템 표(유니크 + 아바타). 확률 공개(PRD 8절) */
export function huntTable(): HuntRow[] {
  const out: HuntRow[] = [];
  for (let region = 1; region <= REGIONS_90D.length; region++) for (const tier of ['mid', 'final'] as const) {
    const t = bossTarget(region, tier);
    out.push({ region, regionName: REGIONS_90D[region - 1].name, tier, boss: t.boss, stageId: t.stageId, playable: region <= PLAYABLE_REGIONS,
      drops: [...namedFor(region, tier).map(uniqueDrop), ...avatarDrops(region, tier)] });
  }
  return out;
}

// ── 도감(PRD 2.2) ──
export interface CodexView {
  readonly uniques: { readonly found: number; readonly total: number };
  readonly avatars: { readonly found: number; readonly total: number };
}
/** 도감 수: 한 번이라도 얻은 이름 있는 유니크 + 지금 가진 아바타(정의가 있는 것만) */
export function codexOf(foundUniques: readonly string[], ownedAvatars: readonly string[]): CodexView {
  const nu = new Set(NAMED_UNIQUES.map((u) => u.id)), av = new Set(AVATARS.map((a) => a.id));
  return {
    uniques: { found: new Set(foundUniques.filter((id) => nu.has(id))).size, total: nu.size },
    avatars: { found: new Set(ownedAvatars.filter((id) => av.has(id))).size, total: av.size },
  };
}

// ── 도감 단계 보상(PRD 2.2, E3): 도감 수 = 유니크 + 아바타. 전투 능력치는 주지 않는다 ──
export const CODEX_STEPS = [5, 10, 20] as const;
export type CodexStep = (typeof CODEX_STEPS)[number];
/** 5종 보상: 색마다 가장 낮은 등급(깨진) 보석 이만큼 */
export const CODEX_GEM_EACH = 2;
export const CODEX_TITLE = '도감 수집가';
export interface CodexRewardDef { readonly step: CodexStep; readonly kind: 'gems' | 'title' | 'aura'; readonly name: string; readonly desc: string }
export const CODEX_REWARDS: readonly CodexRewardDef[] = [
  { step: 5, kind: 'gems', name: '깨진 보석 꾸러미', desc: `홍옥·청옥·황옥 깨진 보석 각 ${CODEX_GEM_EACH}개` },
  { step: 10, kind: 'title', name: `칭호 「${CODEX_TITLE}」`, desc: '모험 창에 이름표로 표시돼요 (능력치 없음)' },
  { step: 20, kind: 'aura', name: '수집가의 황금 오라', desc: '발밑 오라 색 아바타 (능력치 없음)' },
];
export const CODEX_AURA_AVATAR = CODEX_AURA_ID;
export function codexRewardOf(step: number): CodexRewardDef | undefined { return CODEX_REWARDS.find((r) => r.step === step); }
export function codexCount(cx: CodexView): number { return cx.uniques.found + cx.avatars.found; }
export interface CodexStepView { readonly reward: CodexRewardDef; readonly reached: boolean; readonly claimed: boolean; readonly can: boolean; readonly need: number }
/** 단계별 진행: 도달(수 ≥ 단계)·받음·지금 받을 수 있음 */
export function codexSteps(count: number, claimed: readonly number[]): CodexStepView[] {
  return CODEX_REWARDS.map((reward) => {
    const reached = count >= reward.step, done = claimed.includes(reward.step);
    return { reward, reached, claimed: done, can: reached && !done, need: Math.max(0, reward.step - count) };
  });
}
/** 받은 단계에서 나오는 칭호(10종을 받았으면) */
export function codexTitle(claimed: readonly number[]): string | null { return claimed.includes(10) ? CODEX_TITLE : null; }

/** UI가 읽는 모험 상태(GameSim view.adventure) */
export interface AdventureView {
  readonly revision: number;
  /** 완료한 현상금 id */
  readonly done: readonly string[];
  /** 한 번이라도 얻은 이름 있는 유니크 id */
  readonly found: readonly string[];
  /** 오늘(KST) 날짜 번호 */
  readonly day: number;
  readonly highestTown: number;
  /** 받은 도감 단계(5·10·20) */
  readonly claimed?: readonly number[];
  /** 마지막으로 받은 도감 단계(결과 띠). 없으면 null */
  readonly lastClaim?: { readonly step: number; readonly serial: number } | null;
}
